import React, { FC } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Divider,
} from '@material-ui/core';
import { Button, Label, Link, LoadingIndicator } from '@uhg-harmony/uitk-react';
import styled from 'styled-components';
import { format } from 'date-fns';
import { ArrowBackIcon } from '@uhg-harmony/uitk-icons-react';
import { ButtonWrapper } from 'builder/scenes/SiteBuilder/PagesPanel/PagesPanel';
import { toast } from 'react-toastify';
import { executeSiteRevert } from './VersionHistoryDialog.util';

export enum SiteListDialogType {
  UNPUBLISH = 'UNPUBLISH',
  DELETE = 'DELETE',
  CANNOT_DELETE = 'CANNOT_DELETE',
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-basis: auto;
  margin-top: 0.4em;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;

const DetailedPublishNotes = styled.div`
  margin-left: 24px;
  margin-top: inherit;
`;

const StyledBulletDiv = styled.div`
  margin-left: 24px;
  font-family: 'OptumSans';
`;

const StyledButton = styled(Button)`
  width: 20%;
`;

const StyledDivider = styled(Divider)`
  margin-top: 8px;
`;

export const CustomDialog = styled(Dialog)`
  & .MuiTypography-root {
    font-family: 'OptumSans';
  }
  .MuiDialog-container {
    height: 60%;
  }
  .MuiDialog-paperWidthSm {
    max-width: 512px;
  }
`;

const CustomLabel = styled(Label)`
  white-space: pre-line;
  margin-left: 22px;
`;

const CustomTypography = styled(Typography)`
  white-space: pre-line;
`;

const CustomLink = styled(Link)`
  margin-left: 24px;
  margin-top: 1rem;
  color: ${p => p.theme.colors.aux.darkBlue};
  font-weight: 500;
  font-family: 'OptumSans';
`;

export interface VersionHistoryDialogProps {
  open?: boolean;
  siteVersionHistory?: Array<SitePublishedMessages>;
  siteVersionsPending?: boolean;
  onClose?: () => void;
  updateSite?: (siteId: number, site: Site) => void;
  updateCurrentPage?: (siteId: number, pageVersion: PageVersion) => void;
  siteId: number;
  saveSite?: (siteId: number) => void;
  fetchPagesByTime: (siteId: number, time: number) => void;
  allSiteVersions: Array<Site>;
  saveSitePages?: (pages: Array<Page>) => void;
}

export const VersionHistoryDialog: FC<VersionHistoryDialogProps> = (
  props: VersionHistoryDialogProps
) => {
  const {
    updateSite,
    saveSite,
    siteId,
    fetchPagesByTime,
    open,
    siteVersionHistory,
    siteVersionsPending,
    allSiteVersions,
    onClose,
  } = props;
  const [
    selectedSiteVersion,
    setSelectedSiteVersion,
  ] = React.useState<SitePublishedMessages | null>(null);
  const [isReverting, setIsReverting] = React.useState(false);
  const handleViewChangeClick = (siteVersion: SitePublishedMessages) => {
    setSelectedSiteVersion(siteVersion);
  };

  const getListOfMessages = (publishMessage: string) => {
    return publishMessage.split(',');
  };

  const revertClick = async () => {
    const revertSiteVersion = allSiteVersions.find(
      site => site.id === selectedSiteVersion?.siteVersionId
    );
    if (!revertSiteVersion) return;

    setIsReverting(true);
    try {
      await executeSiteRevert({
        siteId,
        revertSiteVersion,
        updateSite,
        fetchPagesByTime,
        saveSite,
      });
      setSelectedSiteVersion(null);
    } catch {
      toast('Revert failed. Please try again.', {
        type: 'error',
        theme: 'colored',
      });
    } finally {
      setIsReverting(false);
    }
  };

  const cancelClick = () => {
    onClose();
  };

  return (
    <CustomDialog aria-labelledby="confirmation-dialog-title" open={open}>
      <DialogTitle id="confirmation-dialog-title">
        {'Site Version History'}
      </DialogTitle>
      <DialogContent>
        <div>
          <Typography variant="body1">
            {
              'Restore a previously published version of your site. When restoring a site version, customization and design changes are reverted back to the previous version.'
            }
          </Typography>
        </div>
        {siteVersionsPending || !siteVersionHistory ? (
          <div>
            <LoadingIndicator loading={true} size="m" />
            <Typography variant="body2" color="textSecondary" align={'center'}>
              {
                'Loading site versions... This may take a moment depending on the number of published versions.'
              }
            </Typography>
          </div>
        ) : (
          <>
            {selectedSiteVersion && (
              <CustomLink
                iconLeft
                icon={<ArrowBackIcon />}
                onClick={() => setSelectedSiteVersion(null)}
              >
                Back to Full Site History
              </CustomLink>
            )}
            <Divider />
            {selectedSiteVersion && (
              <DetailedPublishNotes>
                <CustomTypography variant="body1">
                  {format(
                    new Date(selectedSiteVersion.lastModifiedDate),
                    'PPp'
                  ) +
                    '\n' +
                    'Published by ' +
                    selectedSiteVersion.lastModifiedByUser}
                </CustomTypography>
                <StyledBulletDiv>
                  {getListOfMessages(selectedSiteVersion.publishMessage).map(
                    message => (
                      <li key={selectedSiteVersion.siteVersionId}>{message}</li>
                    )
                  )}
                </StyledBulletDiv>
              </DetailedPublishNotes>
            )}
            {!selectedSiteVersion &&
              siteVersionHistory.map(siteVersion => (
                <React.Fragment key={siteVersion.lastModifiedDate}>
                  <Wrapper>
                    <CustomLabel>
                      {format(new Date(siteVersion.lastModifiedDate), 'PPp') +
                        '\n' +
                        'Published by ' +
                        siteVersion.lastModifiedByUser}
                    </CustomLabel>
                    <Button
                      onClick={() => handleViewChangeClick(siteVersion)}
                      data-test-id={`view-changes-button-${siteVersion.siteVersionId}`}
                    >
                      View Changes
                    </Button>
                  </Wrapper>
                  <Divider />
                </React.Fragment>
              ))}
            {selectedSiteVersion && (
              <>
                <StyledDivider />
              </>
            )}
            <ButtonWrapper>
              {selectedSiteVersion && (
                <Button
                  onClick={revertClick}
                  data-test-id="revert-button"
                  disabled={isReverting}
                >
                  {isReverting ? 'Reverting...' : 'Revert'}
                </Button>
              )}
              <StyledButton
                onClick={cancelClick}
                block
                className={'mt-base'}
                data-test-id="cancel-button"
              >
                Cancel
              </StyledButton>
            </ButtonWrapper>
          </>
        )}
      </DialogContent>
    </CustomDialog>
  );
};

export default VersionHistoryDialog;
